import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UsersIcon,
  CogIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  CommandLineIcon,
  BoltIcon,
  PhotoIcon,
  BookOpenIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ServerStackIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import useAuth from "../hooks/useAuth";

// Navigation structure reverted to the flat list
import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";

const navigation = [
  { name: "Users", href: "/admin-v2/users", icon: UsersIcon },
  {
    name: "Bot Strategy",
    href: "/admin-v2/bot-strategy",
    icon: CommandLineIcon,
  },
  { name: "Ad Management", href: "/admin-v2/ad-management", icon: PhotoIcon },
  {
    name: "Course Management",
    href: "/admin-v2/course-management",
    icon: BookOpenIcon,
  },
  {
    name: "Article Management",
    href: "/admin-v2/article",
    icon: DocumentTextIcon,
  },
  {
    name: "Events Management",
    href: "/admin-v2/events",
    icon: CalendarIcon,
  },
  {
    name: "Pairs Management",
    href: "/admin-v2/pairs-management",
    icon: ArrowsRightLeftIcon,
  },
  {
    name: "Subscriptions",
    href: "/admin-v2/subscriptions",
    icon: CurrencyDollarIcon,
  },
  { name: "Referrals", href: "/admin-v2/referrals", icon: UserGroupIcon },
  {
    name: "Suggestions",
    href: "/admin-v2/suggestions",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    name: "Proxy Management",
    href: "/admin-v2/proxies",
    icon: ServerStackIcon,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();
  const { admin, logout } = useAuth();

  const handleMouseEnter = () => {
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    setIsCollapsed(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      // The logout function already handles navigation to "/"
    } catch (error) {
      console.error("Logout failed:", error);
      // You might want to display an error message to the user here
    }
  };

  return (
    <div className="h-screen flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/80 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        // className={`fixed inset-y-0 left-0 z-50 w-72 bg-gray-900 transform ${
        className={`fixed inset-y-0 left-0 bg-gray-900 transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out md:hidden`}
      >
        <div className="flex items-center justify-between h-16 px-4 bg-gray-800 flex-shrink-0">
          <div className="text-xl font-bold text-white ">Xenorize Admin</div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="px-2 py-4 space-y-1">
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/admin-v2" &&
                location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.name}
                to={item.href}
                className={classNames(
                  isActive
                    ? "bg-gray-800 text-white"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white",
                  "group flex items-center px-2 py-2 text-base font-medium rounded-md",
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon
                  className={classNames(
                    isActive
                      ? "text-white"
                      : "text-gray-400 group-hover:text-white",
                    "mr-4 h-6 w-6",
                  )}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div
        className={classNames(
          // "hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ease-in-out",
          "hidden md:flex md:flex-col md:inset-y-0 transition-all duration-300 ease-in-out",
          isCollapsed ? "md:w-20" : "md:w-72",
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex flex-col h-full bg-[linear-gradient(to_bottom_left,#413072,#08080B)]">
          <div className="flex items-center justify-center h-16 bg-[transprent] flex-shrink-0">
            {isCollapsed ? (
              <BoltIcon className="h-8 w-8 text-white" />
            ) : (
              <div className="text-xl font-bold text-white whitespace-nowrap">
                Xenorize Admin
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="px-3 py-4 space-y-2">
              {navigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== "/admin-v2" &&
                    location.pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={classNames(
                      isActive
                        ? "bg-gray-800 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white",
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                    )}
                  >
                    <item.icon
                      className={classNames(
                        isActive
                          ? "text-white"
                          : "text-gray-400 group-hover:text-white",
                        // --- UNIFIED ICON SIZE AND DYNAMIC MARGIN ---
                        "h-6 w-6 flex-shrink-0",
                        isCollapsed ? "mx-auto" : "mr-3",
                      )}
                    />
                    <span
                      className={classNames(
                        isCollapsed
                          ? "hidden whitespace-nowrap"
                          : "inline whitespace-nowrap ",
                      )}
                    >
                      {item.name}
                    </span>
                  </Link>
                );
              })}
            </nav>
            <div
              className={`text-gray-300 hover:bg-gray-700 hover:text-white group ${
                isCollapsed ? "justify-center" : "!justify-start"
              }   flex items-center px-5 py-2 text-base font-medium rounded-md cursor-pointer`}
              onClick={handleLogout}
            >
              <Bars3Icon className=" h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-white flex justify-center" />{" "}
              {/* You might want a different icon for logout */}
              <span
                className={`${isCollapsed ? "hidden" : "inline-block"} ml-4`}
              >
                Logout
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className={classNames(
          "flex flex-col flex-1 transition-all duration-300 ease-in-out ",
          // isCollapsed ? "md:pl-20" : "md:pl-72"
          isCollapsed ? "w-[calc(100%-5rem)]" : "w-[calc(100%-18rem)]",
        )}
      >
        <div className="sticky top-0 z-10 flex items-center h-16 bg-white border-b border-gray-200 px-4 md:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 md:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="ml-4 md:ml-0">
            <h1 className="text-lg font-semibold">Admin Panel</h1>
          </div>
          <div className="ml-auto flex items-center">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-sm font-medium">
                  {admin?.name[0]?.toUpperCase() ?? "A"}
                </span>
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">
                {admin?.name ?? "Admin"}
              </span>
            </div>
          </div>
        </div>

        {/* <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 "> */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 bg-gray-100 ">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
